import { getApiErrorStatus } from "../api/errorUtils";

/**
 * Pure presentation and form rules for the Customers area. Kept out of the
 * pages so they can be tested without rendering.
 */

export const CUSTOMERS_PAGE_SIZE = 20;

/** Mirrors the backend's COMPANY_MANAGER_ROLES; the server still enforces it. */
export const CUSTOMER_EDITOR_ROLES = ["manager", "admin", "owner"];

/** Staff never get an edit form they could not save. Unknown/loading role -> no. */
export function canEditCustomerProfile(role) {
  return CUSTOMER_EDITOR_ROLES.includes(String(role || "").trim().toLowerCase());
}

/** Filters derived from order history - there is no stored "customer status". */
export const CUSTOMER_ACTIVITY_OPTIONS = [
  { value: "", labelKey: "customers.activityAll" },
  { value: "active", labelKey: "customers.activityActive" },
  { value: "delivered", labelKey: "customers.activityDelivered" },
  { value: "cancelled", labelKey: "customers.activityCancelled" },
  { value: "returned_to_sender", labelKey: "customers.activityReturned" },
];

export const CUSTOMER_SORT_OPTIONS = [
  { value: "newest", labelKey: "customers.sortNewest" },
  { value: "oldest", labelKey: "customers.sortOldest" },
  { value: "most_orders", labelKey: "customers.sortMostOrders" },
  { value: "last_order", labelKey: "customers.sortLastOrder" },
];

const ACTIVITY_VALUES = new Set(CUSTOMER_ACTIVITY_OPTIONS.map((option) => option.value).filter(Boolean));
const SORT_VALUES = new Set(CUSTOMER_SORT_OPTIONS.map((option) => option.value));
const DEFAULT_SORT = "newest";

/** "Blend K." -> "BK", "elira" -> "E". Never a made-up picture. */
export function customerInitials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return "?";
  }

  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toLocaleUpperCase("sq");
}

/** A stable colour tile per name, so the same customer always looks the same. */
export function avatarToneFor(name) {
  const text = String(name || "");
  let sum = 0;
  for (let index = 0; index < text.length; index += 1) {
    sum += text.charCodeAt(index);
  }
  return sum % 6;
}

export function readCustomersListState(searchParams) {
  const page = Number(searchParams.get("page"));
  const activity = String(searchParams.get("activity") || "");
  const sort = String(searchParams.get("sort") || "");

  return {
    search: String(searchParams.get("search") || "").trim(),
    city: String(searchParams.get("city") || "").trim(),
    activity: ACTIVITY_VALUES.has(activity) ? activity : "",
    sort: SORT_VALUES.has(sort) ? sort : DEFAULT_SORT,
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

/** URL state without defaults, so a plain list keeps a clean URL. */
export function writeCustomersListState(state) {
  const next = new URLSearchParams();
  if (state.search) next.set("search", state.search);
  if (state.city) next.set("city", state.city);
  if (state.activity) next.set("activity", state.activity);
  if (state.sort && state.sort !== DEFAULT_SORT) next.set("sort", state.sort);
  if (state.page > 1) next.set("page", String(state.page));
  return next;
}

export function buildCustomersListParams(state) {
  return {
    search: state.search || undefined,
    city: state.city || undefined,
    activity: state.activity || undefined,
    sort: state.sort && state.sort !== DEFAULT_SORT ? state.sort : undefined,
    page: state.page > 1 ? state.page : undefined,
    pageSize: CUSTOMERS_PAGE_SIZE,
  };
}

export function hasActiveCustomerFilters(state) {
  return Boolean(state.search || state.city || state.activity);
}

export function showingRange({ page, pageSize, total }) {
  if (!total) {
    return { from: 0, to: 0 };
  }

  const from = (page - 1) * pageSize + 1;
  return { from, to: Math.min(page * pageSize, total) };
}

export function formatCustomerDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("sq-AL", { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

export function formatCustomerTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("sq-AL", { hour: "2-digit", minute: "2-digit" }).format(date);
}

export function phoneDigits(value) {
  return String(value || "").replace(/\D+/g, "");
}

export function hasPhoneChanged(customer, draft) {
  return phoneDigits(customer?.phone) !== phoneDigits(draft?.phone);
}

export function buildEditDraftFromCustomer(customer) {
  return {
    name: customer?.name || "",
    phone: customer?.phone || "",
    email: customer?.email || "",
    city: customer?.city || "",
    country: customer?.country || "",
    address: customer?.address || "",
  };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateCustomerProfile(draft) {
  const required = (value, key) => (String(value || "").trim() ? null : key);
  const phone = String(draft.phone || "").trim();
  const email = String(draft.email || "").trim();

  return {
    name: required(draft.name, "customers.nameRequired"),
    phone: !phone
      ? "customers.phoneRequired"
      : phoneDigits(phone).length < 6
        ? "customers.phoneInvalid"
        : null,
    city: required(draft.city, "customers.cityRequired"),
    address: required(draft.address, "customers.addressRequired"),
    email: email && !EMAIL_PATTERN.test(email) ? "customers.emailInvalid" : null,
  };
}

export function hasProfileErrors(errors) {
  return Object.values(errors).some(Boolean);
}

/** Only the fields that actually changed, in the API's field names. */
export function buildCustomerProfilePatch(customer, draft) {
  const patch = {};
  const changed = (next, current) => String(next || "").trim() !== String(current || "").trim();

  if (changed(draft.name, customer.name)) patch.recipientName = draft.name.trim();
  if (changed(draft.phone, customer.phone)) patch.phone = draft.phone.trim();
  if (changed(draft.city, customer.city)) patch.city = draft.city.trim();
  if (changed(draft.address, customer.address)) patch.address = draft.address.trim();
  if (draft.country && changed(draft.country, customer.country)) patch.country = draft.country;

  const nextEmail = String(draft.email || "").trim() || null;
  if (nextEmail !== (customer.email || null)) patch.email = nextEmail;

  return patch;
}

/** Albanian copy for a failed save; the raw API text is never shown. */
export function describeCustomerSaveError(error) {
  const status = getApiErrorStatus(error);
  if (status === 409) return "customers.conflictExact";
  if (status === 403) return "customers.permissionDenied";
  if (status === 400 || status === 422) return "customers.invalidData";
  return "customers.saveFailed";
}
